export enum OrderStatus {
  Pending = "Pending",
  draft = "draft",
  application_submitted = "application_submitted",
  under_review = "under_review",
  on_hold = "on_hold",
  approved = "approved",
  rejected = "rejected",
  cancelled = "cancelled",
  refunded = "refunded",
}

export enum ProcessingType {
  Normal = "Normal",
  Rush = "Rush",
}

export enum PaymentStatus {
  pending = "pending",
  processing = "processing",
  paid = "paid",
  failed = "failed",
  refunded = "refunded",
  cancelled = "cancelled",
}

export type PayoutStatus =
  | "pending"
  | "ready_to_be_paid"
  | "paid"
  | "completed"
  | "cancelled";
export type RecipientType = "platform" | "association" | "management_company";

export enum PaymentMethod {
  "Credit Card" = "Credit Card",
  "Debit Card" = "Debit Card",
  "Bank Transfer" = "Bank Transfer",
  PayPal = "PayPal",
  Stripe = "Stripe",
  Cash = "Cash",
  Check = "Check",
  Other = "Other",
}

export enum OrderItemType {
  document_fee = "document_fee",
  transaction_fee = "transaction_fee",
  FEE = "FEE",
}

import { AssociationType, TaxClassification } from "./Association";
import { Property } from "./Property";

export interface OrderItem {
  id: number;
  order_id: number;
  item_type: OrderItemType;
  reference_id: number | null;
  amount: number;
  quantity: number;
  subtotal_amount: number;
  label: string | null;
  created_at: Date;
  apportionments?: {
    id: number;
    party_type: string;
    percentage: number;
    amount: number;
  }[];
}

export interface OrderPayment {
  id: number;
  order_id: number;
  status: PaymentStatus;
  payment_method: PaymentMethod;
  payment_reference: string | null;
  amount: number;
  created_at: Date;
  updated_at: Date;
}

export interface FormFieldValue {
  field_id: number;
  value: string;
  /**
   * The FIELD's own repeat slot (pet #2, vehicle #2). NOT the applicant.
   */
  index?: number;
  /**
   * WHO signed this value. Present only on signature/initials values from
   * multi-applicant orders. Orthogonal to `index` — the same (field_id, index)
   * can hold one value per signer.
   */
  applicant_uid?: string | null;
  section_id?: number;
  field_title?: string;
  section_title?: string;
  step_title?: string;
}

export interface OrderDocumentField {
  field_id: number;
  value: any;
  /**
   * The FIELD's own repeat slot (pet #2, vehicle #2, occupant #2).
   * NOT the applicant — see applicant_uid.
   */
  index?: number;
  /**
   * WHO signed this value. Present only on signature/initials values from
   * multi-applicant orders; absent on legacy single-signer values and on all
   * non-signature fields. Orthogonal to `index`.
   */
  applicant_uid?: string | null;
  section_id?: number;
  field_title?: string;
  section_title?: string;
  step_title?: string;
  field_order?: number;
  section_order?: number;
  step_order?: number;
  field_type?: string;
  id?: number;
  type?: string;
}

export interface OrderDocumentSection {
  section_id: number;
  title: string;
  order: number;
  fields: OrderDocumentField[];
  status?: "completed" | "pending";
}

export interface OrderDocument {
  id: number;
  document_id: number;
  name: string;
  sections?: OrderDocumentSection[];
  fields?: FormFieldValue[];
  document?: {
    id: number;
    name: string;
    document_type_id: number;
  };
  created_at?: string;
  updated_at?: string; // Add this property to match the actual data
  order_id: number;
  association_id: number; // Add association_id property
  parent_id?: number; // Reference to parent order document for updates
  released?: boolean; // Add released status
  uploaded_file_path?: string; // Path to uploaded file instead of form data
}

export interface Association {
  id: number;
  name: string;
  billing_address?: string;
  billing_city?: string;
  billing_state?: string;
  billing_zip?: string;
  management_company_id: number;
  association_type: AssociationType;
  email: string | null;
  phone: string | null;
  ein: string | null;
  tax_classification: TaxClassification | null;
  billing_unit: string | null;
  default_property_address: string | null;
  default_property_city: string | null;
  default_property_state: string | null;
  default_property_zip: string | null;
  default_property_latitude: number | null;
  default_property_longitude: number | null;
  bank_account: string | null;
  routing: string | null;
  parent_association_id: number | null;
  pricing_table_id: number | null;
  document_groups?: number[];
  external_id: string;
  created_at: Date;
  updated_at: Date;
}

export interface StatusHistoryItem {
  id: number;
  old_status: string;
  new_status: string;
  note?: string;
  created_at: Date;
}

export interface Order {
  id: number;
  customer_id: number;
  customer_type_id: number;
  association_id: number;
  property_id?: number;
  parent_id?: number;
  status: OrderStatus;
  processing_type: string;
  total_amount: number;
  expected_delivery_date?: string;
  ip_address?: string;
  created_at: string;
  updated_at: string;
  items?: OrderItem[];
  at_closing_items?: OrderItem[];
  payments?: OrderPayment[];
  requested_documents?: OrderDocument[];
  status_history?: StatusHistoryItem[];
  customer?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    auth_user_id?: string;
    created_at?: Date | string;
    updated_at?: Date | string;
  };
  association?: Association;
  property?: Property;
  refunds?: {
    id: number;
    amount: number;
    status: string;
    created_at: string;
    items: {
      item_id: number;
      quantity: number;
    }[];
  }[];
}

export interface OrderSearchParams {
  search?: string;
  customer_id?: number;
  association_id?: number;
  status?: OrderStatus | OrderStatus[];
  processing_type?: ProcessingType;
  limit?: number;
  offset?: number;
  include_customer?: boolean;
  management_company_id?: number;
}

export interface UIOrder extends Order {
  downloadLinks: {
    id: string;
    name: string;
    docId: string;
  }[];
  associationName: string;
}

export interface PayoutOrder {
  id: number;
  order_id: number; // Kept for compatibility and indirect reference
  payment_id: number; // Primary reference - links to order_payments
  order_item_id?: number;
  order_item?: {
    id: number;
    label?: string;
    item_type: string;
    amount: number;
    quantity: number;
  };
  order?: {
    id: number;
    status: OrderStatus;
    total_amount: number;
    association_id: number;
    created_at: string;
    customer?: {
      id: number;
      first_name: string;
      last_name: string;
      email: string;
      auth_user_id?: string;
      created_at?: Date | string;
      updated_at?: Date | string;
    };
  };
  payment?: {
    id: number;
    order_id: number;
    status: PaymentStatus;
    payment_method: PaymentMethod;
    amount: number;
    created_at: string;
  };
  amount: number;
  status: PayoutStatus;
  approval_status?: 'pending' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
  paid_at?: string;
  payout_id?: number;
  payout?: {
    id: number;
    status: PayoutStatus;
    amount: number;
    created_at: string;
  };
  recipient_type: RecipientType;
  recipient_id: number | null;
  recipient_name?: string;
  association_id?: number;
  management_company_id?: number;
  fees_apportion_party_id?: number;
}

export interface Payout {
  id: number;
  recipient_type: RecipientType;
  recipient_id: number | null;
  recipient_name?: string;
  amount: number;
  status: PayoutStatus;
  created_at: string;
  updated_at: string;
  paid_at?: string;
  payment_reference?: string;
  association_id?: number;
  management_company_id?: number;
  bank_account?: string;
  routing_number?: string;
  bank_name?: string;
  account_type?: string;
  wire_instructions?: string;
  fees_apportion_party_id?: number;
}

export interface OrderRefund {
  id: number;
  order_id: number;
  amount: number;
  status: string;
  created_at: string;
  items: {
    item_id: number;
    quantity: number;
  }[];
}

export interface OrderWithRefunds extends Order {
  refunds?: OrderRefund[];
}
